import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { M365User } from '../../types/m365Users'

interface ResetPasswordModalProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onClose: () => void
}

export function ResetPasswordModal({ user, customerId, customerTenantId, onClose }: ResetPasswordModalProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleResetPassword = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use customer tenant token for password reset
      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.resetM365UserPassword(token, customerId, user.id)
      setTemporaryPassword(response.temporaryPassword)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyPassword = async () => {
    if (temporaryPassword) {
      await navigator.clipboard.writeText(temporaryPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:align-middle">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {temporaryPassword ? (
              // Success state - show temporary password
              <div>
                <div className="flex items-center justify-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <h4 className="text-center text-lg font-medium text-gray-900 mb-2">Password Reset Successfully</h4>
                <p className="text-center text-sm text-gray-500 mb-4">
                  The user must change this password at next sign-in.
                </p>

                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Temporary Password</label>
                  <div className="flex items-center">
                    <code className="flex-1 text-lg font-mono bg-white text-gray-900 px-3 py-2 rounded border border-gray-300">
                      {temporaryPassword}
                    </code>
                    <button
                      onClick={handleCopyPassword}
                      className="ml-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {copied && (
                    <p className="text-xs text-green-600 mt-1">Copied to clipboard!</p>
                  )}
                </div>

                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-yellow-700">
                      Share this password securely with the user. It will not be shown again.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Confirmation state
              <div>
                <div className="flex items-center mb-4">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                    {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                    <p className="text-sm text-gray-500">{user.userPrincipalName}</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Are you sure?</h4>
                      <p className="mt-1 text-sm text-yellow-700">
                        This will generate a new temporary password. The user will be required to change it on next sign-in.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {temporaryPassword ? 'Close' : 'Cancel'}
            </button>
            {!temporaryPassword && (
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
